import React, { useState, useContext, useEffect, useMemo } from "react";
import { getLoggedInUser } from "../../APIManager";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  Checkbox,
  Alert,
  Radio,
  Upload,
  Divider,
  Modal,
  Card,
  Col,
  Row,
  List,
  Skeleton,
  Typography,
  Switch,
  Space,
  Statistic,
} from "antd";
import styles from "./HomePage.module.css";
import { GetRowers, GetSchools, Logout } from "../../APIManager";
import { ListOfEvents } from "../../constants";

import {
  UserOutlined,
  BankOutlined,
  TeamOutlined,
  TrophyOutlined,
  ManOutlined,
  WomanOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  IdcardOutlined,
} from "@ant-design/icons";

import PieChart from "./pieChart";
import ColumnChart from "./columnChart";
import CartCard from "./ChartCard";

import { useHistory } from "react-router-dom";
import { UserContext } from "../../UserContext"

import { GetRaceClass, GetRaceGender, GetRaceAgeGroup } from "../../utility";
import { use } from "react";
import ChartCard from "./ChartCard";

const { Text } = Typography;

const HomePage = () => {
  const [schools, SetSchools] = useState([]);
  const [rowers, SetRowers] = useState([]);

  const U25Rowers = rowers.filter((rower) => {
    return rower.AgeGroup === "Under 25";
  });

  const U21Rowers = rowers.filter((rower) => {
    return rower.AgeGroup === "Under 21";
  });
  const U18Rowers = rowers.filter((rower) => {
    return rower.AgeGroup === "Under 18";
  });
  const U15Rowers = rowers.filter((rower) => {
    return rower.AgeGroup === "Under 15";
  });

  const FemaleRowers = rowers.filter((rower) => {
    return rower.Gender === "Female";
  });
  const MaleRowers = rowers.filter((rower) => {
    return rower.Gender === "Male";
  });

  useEffect(() => {
    GetRowers().then((res) => {
      SetRowers(res.data);
    });

    GetSchools().then((res) => {
      SetSchools(res.data);
    });
  }, []);

  let history = useHistory();
  let [userContext, SetUserContext] = useContext(UserContext);

  return (
    <div>
      <Button
        size={"large"}
        type=""
        onClick={() => {
          Logout().then((res) => {
            SetUserContext(res.data);
            history.push("/login");
          });
        }}
      >
        Log Out
      </Button>
      <ChartCard title={"Event Overview"} icon={AppstoreOutlined}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <StatTile
              title="Schools"
              value={schools.length}
              icon={<BankOutlined />}
              color="#1890ff"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <StatTile
              title="Rowers"
              value={rowers.length}
              icon={<TeamOutlined />}
              color="#52c41a"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <StatTile
              title="Events"
              value={ListOfEvents.length}
              icon={<TrophyOutlined />}
              color="#faad14"
            />
          </Col>
        </Row>
        <Divider style={{ margin: "24px 0" }} />

        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <StatTile
              title="U25"
              value={U25Rowers.length}
              icon={<IdcardOutlined />}
              color={ageGroupColors.Under25}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              title="U21"
              value={U21Rowers.length}
              icon={<IdcardOutlined />}
              color={ageGroupColors.Under21}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              title="U18"
              value={U18Rowers.length}
              icon={<IdcardOutlined />}
              color={ageGroupColors.Under18}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              title="U15"
              value={U15Rowers.length}
              icon={<IdcardOutlined />}
              color={ageGroupColors.Under15}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              title="Male Rowers"
              value={MaleRowers.length}
              icon={<ManOutlined />}
              color="#1890ff"
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              title="Female Rowers"
              value={FemaleRowers.length}
              icon={<WomanOutlined />}
              color="#eb2f96"
            />
          </Col>
        </Row>
      </ChartCard>
      <br />
      <Charts rowers={rowers} schools={schools} ListOfEvents={ListOfEvents} />
    </div>
  );
};

const Charts = ({ rowers, schools, ListOfEvents }) => {
  const [selectedCategory, setSelectedCategory] = useState("Outdoor");
  const [selectedSchoolType, setSelectedSchoolType] = useState("All");

  // 1. Memoize the base entries calculation
  const entriesByEvent = useMemo(() => {
    const base = ListOfEvents.map((e) => ({ type: e, value: 0 }));

    schools.forEach((s) => {
      s.evententries.forEach((entry) => {
        const index = base.findIndex((e) => e.type === entry);
        if (index !== -1) {
          base[index].value += 1;
        }
      });
    });
    return base;
  }, [schools, ListOfEvents]); // Only re-run if schools or events change

  // 2. Memoize Rowers By Age (Prevents Pie Chart flash)
  const rowersByAge = useMemo(
    () => [
      {
        type: "Under 15",
        value: rowers.filter((r) => r.AgeGroup === "Under 15").length,
      },
      {
        type: "Under 18",
        value: rowers.filter((r) => r.AgeGroup === "Under 18").length,
      },
      {
        type: "Under 21",
        value: rowers.filter((r) => r.AgeGroup === "Under 21").length,
      },
      {
        type: "Under 25",
        value: rowers.filter((r) => r.AgeGroup === "Under 25").length,
      },
    ],
    [rowers],
  );

  // 3. Memoize Rowers By Gender (Prevents Pie Chart flash)
  const rowersByGender = useMemo(
    () => [
      { type: "Male", value: rowers.filter((r) => r.Gender === "Male").length },
      {
        type: "Female",
        value: rowers.filter((r) => r.Gender === "Female").length,
      },
    ],
    [rowers],
  );

  // 4. Memoize School Charts
  const noOfEntriesBySchool = useMemo(
    () =>
      schools.map((s) => ({
        type: s.schoolinitials,
        schoolType: s.schooltype,
        value: s.evententries.length,
        color: s.rowingcolors[0],
      })),
    [schools],
  );

  const noOfRowersBySchool = useMemo(
    () =>
      schools.map((s) => ({
        type: s.schoolinitials,
        value: rowers.filter((r) => r.Team.schoolname === s.schoolname).length,
        schoolType: s.schooltype,
        color: s.rowingcolors[0],
      })),
    [schools, rowers],
  );

  return (
    <>
      {" "}
      <Row gutter={[16, 24]}>
        <Col xs={24} sm={24} md={12} lg={12}>
          <CartCard title="Rowers By Age" icon={UserOutlined}>
            <PieChart
              data={rowersByAge}
              colorMapper={({ type }) => {
                switch (type) {
                  case "Under 15":
                    return ageGroupColors.Under15;
                  case "Under 18":
                    return ageGroupColors.Under18;
                  case "Under 21":
                    return ageGroupColors.Under21;
                  case "Under 25":
                    return ageGroupColors.Under25;
                  default:
                    return undefined;
                }
              }}
            />
          </CartCard>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <CartCard title="Rowers By Gender" icon={UserOutlined}>
            <PieChart
              data={rowersByGender}
              colorMapper={({ type }) => {
                console.log(type);
                return type === "Male" ? "#1890ff" : "#eb2f96";
              }}
            />
          </CartCard>
        </Col>
      </Row>
      <br />
      <Card
        style={{
          marginBottom: 24,
          borderRadius: 12,
          backgroundColor: "#fcfcfcfc",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        }}
        bodyStyle={{ padding: "12px 24px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            Event Entries
          </Typography.Title>

          <Radio.Group
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="Outdoor">Outdoor</Radio.Button>
            <Radio.Button value="Indoor">Indoor</Radio.Button>
          </Radio.Group>
        </div>
      </Card>
      <Row gutter={[16, 24]}>
        {EntriesGraphs.map((graph, index) => {
          // Convert "Under 25" to "U25" for the filter logic
          const ageGroupCode = graph.RaceAgeGroup.replace("Under ", "U");
          const genderLabel = graph.RaceGender === "Male" ? "Boys" : "Girls";

          const filteredData = entriesByEvent.filter(
            (e) =>
              GetRaceClass(e.type) ===
                (selectedCategory === "Indoor"
                  ? "Indoor Race"
                  : "Water Race") &&
              GetRaceGender(e.type) === graph.RaceGender &&
              GetRaceAgeGroup(e.type) === ageGroupCode,
          );

          console.log("Filtered Data:", filteredData);

          const cleanData = filteredData.map((item) => ({
            ...item,
            type: item.type
              .replace(`${graph.RaceAgeGroup} ${genderLabel}`, "")
              .trim(),
          }));

          console.log("Clean Data:", cleanData);

          return (
            <Col key={index} xs={24} sm={24} md={12} lg={12}>
              <CartCard
                title={`${graph.RaceAgeGroup} ${genderLabel} ${selectedCategory} Entries`}
                icon={UserOutlined}
              >
                <ColumnChart
                  height={300}
                  data={cleanData}
                  color={ageGroupColors[graph.RaceAgeGroup.replace(" ", "")]}
                  maxYLabel={
                    getMaxByAgeGroup({
                      entriesByEvent,
                      entriesGraphs: EntriesGraphs,
                      selectedCategory,
                    })[graph.RaceAgeGroup]
                  }
                />
              </CartCard>
            </Col>
          );
        })}
      </Row>
      <br />
      <Card
        style={{
          marginBottom: 24,
          borderRadius: 12,
          backgroundColor: "#fcfcfcfc",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        }}
        bodyStyle={{ padding: "12px 24px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            Schools Charts
          </Typography.Title>

          <Radio.Group
            value={selectedSchoolType}
            onChange={(e) => setSelectedSchoolType(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="All">All</Radio.Button>
            <Radio.Button value="School">School</Radio.Button>
            <Radio.Button value="College">College</Radio.Button>
            <Radio.Button value="University">University</Radio.Button>
          </Radio.Group>
        </div>
      </Card>
      <Row gutter={[16, 24]}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <CartCard title="Entries by School" icon={UserOutlined}>
            <ColumnChart
              height={250}
              data={
                selectedSchoolType === "All"
                  ? noOfEntriesBySchool
                  : noOfEntriesBySchool.filter(
                      (s) => s.schoolType === selectedSchoolType,
                    )
              }
              colorFromSchool
              schools={schools}
            />
          </CartCard>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24}>
          <CartCard title="Rowers by School" icon={UserOutlined}>
            <ColumnChart
              height={250}
              data={
                selectedSchoolType === "All"
                  ? noOfRowersBySchool
                  : noOfRowersBySchool.filter(
                      (s) => s.schoolType === selectedSchoolType,
                    )
              }
              colorFromSchool
              schools={schools}
            />
          </CartCard>
        </Col>
      </Row>
    </>
  );
};

const StatTile = ({ title, value, icon, color }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "20px",
      padding: "12px 0",
    }}
  >
    {/* Larger Icon with soft background */}
    <div
      style={{
        backgroundColor: `${color}15`,
        width: "60px",
        height: "60px",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { style: { color: color, fontSize: "28px" } })}
    </div>

    <Statistic
      title={
        <Text
          style={{
            fontSize: "16px", // Bigger heading
            fontWeight: 600,
            color: "#595959",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {title}
        </Text>
      }
      value={value}
      valueStyle={{
        fontWeight: 800,
        fontSize: "24px", // Bigger value
        lineHeight: "1.2",
      }}
    />
  </div>
);

function getMaxByAgeGroup({ entriesByEvent, entriesGraphs, selectedCategory }) {
  const maxByAgeGroup = {};

  entriesGraphs.forEach((graph) => {
    const ageGroupCode = graph.RaceAgeGroup.replace("Under ", "U");

    const relevantEntries = entriesByEvent.filter(
      (e) =>
        GetRaceClass(e.type) ===
          (selectedCategory === "Indoor" ? "Indoor Race" : "Water Race") &&
        GetRaceAgeGroup(e.type) === ageGroupCode,
    );

    maxByAgeGroup[graph.RaceAgeGroup] = Math.max(
      0,
      ...relevantEntries.map((e) => e.value),
    );
  });

  return maxByAgeGroup;
}

const EntriesGraphs = [
  { RaceAgeGroup: "Under 25", RaceGender: "Male" },
  { RaceAgeGroup: "Under 25", RaceGender: "Female" },
  { RaceAgeGroup: "Under 21", RaceGender: "Male" },
  { RaceAgeGroup: "Under 21", RaceGender: "Female" },
  { RaceAgeGroup: "Under 18", RaceGender: "Male" },
  { RaceAgeGroup: "Under 18", RaceGender: "Female" },
  { RaceAgeGroup: "Under 15", RaceGender: "Male" },
  { RaceAgeGroup: "Under 15", RaceGender: "Female" },
];

const ageGroupColors = {
  Under15: "#22C55E", // Green
  Under18: "#38BDF8", // Light Blue
  Under21: "#EF4444", // Red
  Under25: "#8B5CF6", // Purple
};
export default HomePage;
